import {
	InteractionBar,
	EmailInteractionContent,
	MessagingChatInteractionContent,
	CrmCasePicker,
} from "@/components/molecules/interactionWorkspace";
import { InteractionType } from "@/common/types";
import { WrapUpState, useWrapUpStore } from "@/common/stores/nonpersistingStores/wrapup";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	fetchMessagingCrmContext,
	setMessagingCrmSelectedCases,
} from "@/repository/queryFns/messaging";
import { useTextMonitoring } from "@/hooks/supervisor";

type Props = {
	sessionId: string;
	type: InteractionType;
	entryPointEmail?: string;
};

const SALESFORCE_CONTEXT_POLL_MS = 30_000;
// How many empty (no-context) fetches to make before backing off. ~2 min of polling
// covers a late routing/queue lookup; past that, a context almost never appears.
const MAX_EMPTY_CONTEXT_POLLS = 5;

/**
 * Fetches the Salesforce caller context for the current messaging session via
 * GET /v2/me/messaging/{sessionId}/crm-context. Returns undefined when the
 * session has no context (server 204 / lookup didn't match / SF disabled), which the
 * picker treats as "hide". We refetch on session change (key includes sessionId) so a
 * fresh interaction never inherits the previous one's context. The save callback
 * targets the same session-scoped POST endpoint.
 */
const useMessagingCrmContext = (sessionId: string) => {
	const query = useQuery({
		queryKey: ["messaging-crm-context", sessionId],
		queryFn: () => fetchMessagingCrmContext(sessionId),
		enabled: !!sessionId,
		// SignalR push for messaging SF context isn't wired yet; refetch lightly so
		// late-arriving lookups (e.g. async messaging flow ran SF lookup after the
		// agent connected) surface within a reasonable window.
		//
		// Most sessions never get a Salesforce context (no lookup, no match, or SF
		// off for the tenant). Polling those every 30s forever is wasted multi-tenant
		// load, so back off: once a context exists, keep the 30s refresh (to catch
		// SelectedCaseIds changes); while it's still empty, poll a bounded number of
		// times to catch a late lookup, then stop. A window focus still triggers a
		// one-off refetch (refetchOnWindowFocus) and resumes polling if one appears.
		refetchInterval: (currentQuery) => {
			if (currentQuery.state.data) {
				return SALESFORCE_CONTEXT_POLL_MS;
			}
			const settledPolls =
				currentQuery.state.dataUpdateCount +
				currentQuery.state.errorUpdateCount;
			return settledPolls < MAX_EMPTY_CONTEXT_POLLS
				? SALESFORCE_CONTEXT_POLL_MS
				: false;
		},
		refetchOnWindowFocus: true,
		// Mid-session lookup completions can change SelectedCaseIds; treat data as
		// always-stale so the next interval / focus actually re-fetches.
		staleTime: 0,
	});

	const saveSelected = useCallback(
		(ids: string[]) => setMessagingCrmSelectedCases(sessionId, ids),
		[sessionId]
	);

	return { sfCtx: query.data, saveSelected };
};

export const MessagingInteractionPanel = ({
	sessionId,
	type,
	entryPointEmail,
}: Props) => {
	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);
	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);
	const [isVariableTextOpen, setIsVariableTextOpen] = useState(true);
	const { sfCtx, saveSelected } = useMessagingCrmContext(sessionId);
	// A supervisor monitoring this text interaction (Email/WhatsApp/Webchat) may view the
	// caller's Salesforce context but must not change the handling agent's case selection.
	const isTextMonitoring = useTextMonitoring();

	// Hide the case picker once this interaction is in wrap-up — mirrors the voice panel's
	// gating, but for the opposite-looking reason. The agent's Close action is what BOTH starts
	// wrap-up (MessagingActionsService.CloseInteraction → HandleWrapupEnabling →
	// StartWrapUpOrMakeAvailable) AND fires the Salesforce case-update push
	// (PushInteractionDataAsync, reading SelectedCaseIds) in the same operation. So by the time
	// the agent is in wrap-up the push has already gone out; a picker edit here would update
	// SelectedCaseIds but nothing re-reads it, silently losing the change. Same wrap-up condition
	// the chat content below uses.
	const isThisInteractionInWrapUp =
		!!wrapUp && wrapUp.mediaId === currentInteraction?.id;

	return (
		<>
			<InteractionBar
				type={type}
				isVariableTextOpen={isVariableTextOpen}
				setIsVariableTextOpen={setIsVariableTextOpen}
			/>
			{/* Always mounted; hides ITSELF (renders null) from wrap-up start onward instead of
			    being conditionally unmounted here — the parent-driven unmount during the wrap-up
			    transition stranded the picker's DOM in the workspace (still painted after the
			    interaction closed). See the hidden prop doc on CrmCasePicker.
			    The wrapUpStopped check mirrors the Email content below: when wrap-up ends the
			    wrapUp store clears (isThisInteractionInWrapUp goes false) but
			    currentInteraction.active stays set until the interaction leaves the active list.
			    Keyed by sessionId so switching interactions remounts the picker with a clean
			    slate — without the key its internal state (selected cases, per-match expansion,
			    the dirty/last-sent refs) leaks from the previous interaction into the next. */}
			<CrmCasePicker
				key={sessionId}
				sfCtx={sfCtx}
				onSaveSelectedCases={saveSelected}
				readOnly={isTextMonitoring}
				hidden={
					isThisInteractionInWrapUp ||
					!!currentInteraction?.wrapUpStopped
				}
			/>
			{type == InteractionType.Email &&
				(!wrapUp || (wrapUp && wrapUp.mediaId !== currentInteraction?.id)) &&
				!currentInteraction?.wrapUpStopped && (
					<EmailInteractionContent
						key={sessionId}
						sessionId={sessionId}
						entryPointEmail={entryPointEmail}
						queueId={currentInteraction?.queueId}
						isPreview={false}
					/>
				)}
			{/* Also gated on wrapUpStopped (like the Email content above): ending wrap-up via the
			    X clears the wrapUp store while currentInteraction.active lingers until the
			    interaction leaves the active list, so without the check the conversation
			    re-mounted and flashed for the second or two before the panel unmounted. */}
			{(type == InteractionType.WhatsApp || type == InteractionType.Webchat) &&
				(!wrapUp || (wrapUp && wrapUp.mediaId !== currentInteraction?.id)) &&
				!currentInteraction?.wrapUpStopped && (
					<MessagingChatInteractionContent
						key={sessionId}
						interactionId={sessionId}
						type={type}
						isVariableTextOpen={isVariableTextOpen}
					/>
				)}
		</>
	);
};
